import { useEffect, useMemo, useState } from "react";
import CryptoJS from "crypto-js";
import {
  Building2,
  ClipboardCheck,
  Download,
  FileText,
  Mail,
  Phone,
  UserRound,
  UsersRound,
  Search,
} from "lucide-react";
import { DashboardLayout } from "../components/DashboardLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import FullScreenLoader from "../components/FullScreenLoader";
import { cn } from "../lib/utils";

const ADMIN_USERS_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/admin/users";
const ADMIN_USER_ASSESSMENTS_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/admin/users/assessments";
const ADMIN_ASSESSMENT_INTERVIEWEES_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/admin/users/assessments/interviewees";
const CLIENT_SHARED_SECRET = process.env.REACT_APP_JWT_SECRET || "122Codeman!";

const getRegisterData = () => {
  try {
    const raw = localStorage.getItem("bv.registerResponse");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const extractUsers = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.users)) return data.users;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.users)) return data.data.users;
  return [];
};

const extractAssessments = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.assessments)) return data.assessments;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.assessments)) return data.data.assessments;
  if (Array.isArray(data?.active) || Array.isArray(data?.completed)) {
    return [...(data.active || []), ...(data.completed || [])];
  }
  return [];
};

const extractInterviewees = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.interviewees)) return data.interviewees;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.interviewees)) return data.data.interviewees;
  return [];
};

const getUserId = (user) => String(user?.userId || user?.email || user?.phone);

const getAssessmentStatus = (assessment) => {
  if (assessment?.status) return String(assessment.status);
  if (assessment?.isCompleted === true || assessment?.completed === true) {
    return "completed";
  }
  if (assessment?.active === true) return "active";
  return "active";
};

const getAssessmentId = (assessment) =>
  assessment?.assessmentId || assessment?._id || assessment?.id || "";

const formatUnixDate = (value) => {
  if (!value) return "";
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) return String(value);
  const milliseconds = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
  const date = new Date(milliseconds);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

const decryptPassword = (value) => {
  if (!value) return "";
  try {
    const decrypted = CryptoJS.AES.decrypt(
      String(value),
      CLIENT_SHARED_SECRET,
    ).toString(CryptoJS.enc.Utf8);
    return decrypted || String(value);
  } catch {
    return String(value);
  }
};

const encryptPassword = (value) =>
  CryptoJS.AES.encrypt(String(value), CLIENT_SHARED_SECRET).toString();

const getEditableUser = (user) => ({
  ...(user || {}),
  password: decryptPassword(user?.password),
});

const Admin = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState("");
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState("");
  const [assessmentSearch, setAssessmentSearch] = useState("");
  const [interviewees, setInterviewees] = useState([]);
  const [intervieweeSearch, setIntervieweeSearch] = useState("");
  const [intervieweeLoading, setIntervieweeLoading] = useState(false);
  const [intervieweeError, setIntervieweeError] = useState("");
  const [profileForm, setProfileForm] = useState({});
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteProfileLoading, setDeleteProfileLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState("assessments");
  const [monthlyAllowed, setMonthlyAllowed] = useState([]);
  const [monthlyLoading, setMonthlyLoading] = useState(false);
  const [monthlyError, setMonthlyError] = useState("");
  const [monthlyDialogOpen, setMonthlyDialogOpen] = useState(false);
  const [monthlyForm, setMonthlyForm] = useState({
    month: "",
    year: "",
    allowed: "",
  });
  const [monthlyEditingItem, setMonthlyEditingItem] = useState(null);
  const monthOptions = [
    { value: "1", label: "Jan" },
    { value: "2", label: "Feb" },
    { value: "3", label: "Mar" },
    { value: "4", label: "Apr" },
    { value: "5", label: "May" },
    { value: "6", label: "Jun" },
    { value: "7", label: "Jul" },
    { value: "8", label: "Aug" },
    { value: "9", label: "Sep" },
    { value: "10", label: "Oct" },
    { value: "11", label: "Nov" },
    { value: "12", label: "Dec" },
  ];

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const start = current;
    const end = current + 10;
    const years = [];
    for (let y = start; y <= end; y += 1) years.push(String(y));
    return years;
  }, []);
  const [userSearch, setUserSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const registerData = useMemo(getRegisterData, []);
  const authToken = registerData?.authToken;

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(ADMIN_USERS_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(authToken ? { authorization: authToken } : {}),
          },
          body: JSON.stringify({}),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.success === false) {
          throw new Error(data?.message || "Failed to fetch users.");
        }
        const nextUsers = extractUsers(data);
        setUsers(nextUsers);
        setSelectedUserId((currentId) => currentId || getUserId(nextUsers[0]));
      } catch (e) {
        setError(e.message || "Failed to fetch users.");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [authToken]);

  const filteredUsers = useMemo(() => {
    const searchValue = userSearch.trim().toLowerCase();
    if (!searchValue) return users;
    return users.filter((user) =>
      [
        user?.fullName,
        user?.name,
        user?.email,
        user?.phone,
        user?.companyName,
        user?.role,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(searchValue)),
    );
  }, [userSearch, users]);

  const selectedUser = users.find((user) => getUserId(user) === selectedUserId);
  const selectedAssessment = assessments.find(
    (assessment) =>
      String(
        assessment?._id ||
          assessment?.id ||
          assessment?.assessmentId ||
          assessment?.companyName,
      ) === selectedAssessmentId,
  );

  const filteredInterviewees = useMemo(() => {
    const searchValue = intervieweeSearch.trim().toLowerCase();
    if (!searchValue) return interviewees;
    return interviewees.filter((interviewee) =>
      [
        interviewee?.name,
        interviewee?.fullName,
        interviewee?.email,
        interviewee?.phone,
        interviewee?.role,
        interviewee?.position,
        interviewee?.department,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(searchValue)),
    );
  }, [intervieweeSearch, interviewees]);

  const filteredAssessments = useMemo(() => {
    const searchValue = assessmentSearch.trim().toLowerCase();
    if (!searchValue) return assessments;
    return assessments.filter((assessment) =>
      [
        assessment?.companyName,
        assessment?.title,
        assessment?.keyContact,
        assessment?.contact,
        assessment?.email,
        assessment?.phone,
        assessment?.status,
        assessment?.assessmentId,
        assessment?._id,
        assessment?.id,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(searchValue)),
    );
  }, [assessmentSearch, assessments]);

  useEffect(() => {
    setProfileForm(getEditableUser(selectedUser));
  }, [selectedUser]);

  useEffect(() => {
    if (!selectedUser) {
      setAssessments([]);
      setSelectedAssessmentId("");
      setAssessmentSearch("");
      setInterviewees([]);
      setIntervieweeSearch("");
      return;
    }

    const fetchAssessments = async () => {
      setAssessmentLoading(true);
      setAssessmentError("");
      try {
        const res = await fetch(ADMIN_USER_ASSESSMENTS_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(authToken ? { authorization: authToken } : {}),
          },
          body: JSON.stringify({ user: selectedUser?.email }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.success === false) {
          throw new Error(data?.message || "Failed to fetch assessments.");
        }
        const nextAssessments = extractAssessments(data);
        setAssessments(nextAssessments);
        setSelectedAssessmentId((currentId) => {
          if (
            currentId &&
            nextAssessments.some(
              (assessment) =>
                String(
                  assessment?._id ||
                    assessment?.id ||
                    assessment?.assessmentId ||
                    assessment?.companyName,
                ) === currentId,
            )
          ) {
            return currentId;
          }
          const firstAssessment = nextAssessments[0];
          return firstAssessment
            ? String(
                firstAssessment?._id ||
                  firstAssessment?.id ||
                  firstAssessment?.assessmentId ||
                  firstAssessment?.companyName,
              )
            : "";
        });
      } catch (e) {
        setAssessmentError(e.message || "Failed to fetch assessments.");
        setAssessments([]);
        setSelectedAssessmentId("");
      } finally {
        setAssessmentLoading(false);
      }
    };

    fetchAssessments();
  }, [authToken, selectedUser]);

  useEffect(() => {
    if (!selectedAssessment) {
      setInterviewees([]);
      setIntervieweeSearch("");
      setIntervieweeError("");
      return;
    }

    const fetchInterviewees = async () => {
      setIntervieweeLoading(true);
      setIntervieweeError("");
      try {
        const res = await fetch(ADMIN_ASSESSMENT_INTERVIEWEES_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(authToken ? { authorization: authToken } : {}),
          },
          body: JSON.stringify({
            user: selectedUser?.email,
            assessmentId: getAssessmentId(selectedAssessment),
            assessment: selectedAssessment,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data?.success === false) {
          throw new Error(data?.message || "Failed to fetch interviewees.");
        }
        setInterviewees(extractInterviewees(data));
      } catch (e) {
        setIntervieweeError(e.message || "Failed to fetch interviewees.");
        setInterviewees([]);
      } finally {
        setIntervieweeLoading(false);
      }
    };

    fetchInterviewees();
  }, [authToken, selectedAssessment, selectedUser?.email]);

  const handleProfileChange = (key, value) => {
    setProfileForm((current) => ({ ...current, [key]: value }));
  };

  const handleUpdateProfile = async () => {
    if (!selectedUser) return;
    const email = selectedUser.email || profileForm.email;
    if (!email) {
      setProfileError("Selected user email is required.");
      toast.error("Selected user email is required.");
      return;
    }

    setProfileLoading(true);
    setProfileError("");
    try {
      const payload = {
        ...profileForm,
        email,
        password:
          profileForm.password &&
          profileForm.password !== decryptPassword(selectedUser.password)
            ? encryptPassword(profileForm.password)
            : selectedUser.password || profileForm.password || "",
      };
      const res = await fetch(ADMIN_USERS_URL, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { authorization: authToken } : {}),
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Failed to update user.");
      }
      const updatedUser = data?.user || data?.data || payload;
      setUsers((currentUsers) =>
        currentUsers.map((user) =>
          getUserId(user) === selectedUserId
            ? { ...user, ...updatedUser, email }
            : user,
        ),
      );
      setProfileForm((current) =>
        getEditableUser({ ...current, ...updatedUser, email }),
      );
      toast.success("User profile updated.");
    } catch (e) {
      const message = e.message || "Failed to update user.";
      setProfileError(message);
      toast.error(message);
    } finally {
      setProfileLoading(false);
    }
  };

  const handleDeleteProfile = async () => {
    if (!selectedUserId) return;
    setDeleteProfileLoading(true);
    setProfileError("");
    try {
      const res = await fetch(
        "https://printlogs-17738dc65798.herokuapp.com/bizVantage/admin/users",
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            ...(authToken ? { authorization: authToken } : {}),
          },
          body: JSON.stringify({ userId: selectedUserId }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Failed to delete user.");
      }

      setUsers((currentUsers) =>
        currentUsers.filter((user) => getUserId(user) !== selectedUserId),
      );
      setSelectedUserId("");
      setProfileForm({});
      toast.success("User deleted.");
    } catch (e) {
      const message = e.message || "Failed to delete user.";
      setProfileError(message);
      toast.error(message);
    } finally {
      setDeleteProfileLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const renderProfileField = (key, label, type = "text", disabled = false) => {
    const value = profileForm?.[key];
    const isThumbnailField = key === "img" || key === "companyLogo";

    if (typeof value === "boolean") {
      const statusText =
        key === "active" ? (value ? "Active" : "De-activated") : null;

      return (
        <div className="space-y-2 rounded-lg border p-4">
          <div className="flex items-center justify-between gap-4">
            <Label htmlFor={`profile-${key}`}>{label}</Label>
            <Switch
              id={`profile-${key}`}
              checked={value}
              disabled={disabled}
              onCheckedChange={(checked) => handleProfileChange(key, checked)}
            />
          </div>
          {statusText ? (
            <p className="text-sm text-muted-foreground">{statusText}</p>
          ) : null}
          <p className="text-sm text-muted-foreground">
            Please click on "Update" button to save the changes.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        <Label htmlFor={`profile-${key}`}>{label}</Label>
        <Input
          id={`profile-${key}`}
          type={type}
          value={value ?? ""}
          disabled={isThumbnailField || disabled}
          onChange={(event) => handleProfileChange(key, event.target.value)}
        />
        {isThumbnailField && value ? (
          <div className="mt-2 max-w-[160px] overflow-hidden rounded-md border border-slate-200 bg-white p-1">
            <img
              src={value}
              alt={`${label} thumbnail`}
              className="h-24 w-full object-cover"
            />
          </div>
        ) : null}
      </div>
    );
  };

  const renderPlanOptionField = () => (
    <div className="space-y-2">
      <Label htmlFor="profile-planOption">Plan Option</Label>
      <Select
        value={profileForm?.planOption || ""}
        onValueChange={(value) => handleProfileChange("planOption", value)}
      >
        <SelectTrigger id="profile-planOption">
          <SelectValue placeholder="Select plan" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="Core">Core</SelectItem>
          <SelectItem value="Pro">Pro</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  const renderRoleField = () => (
    <div className="space-y-2">
      <Label htmlFor="profile-role">Role</Label>
      <Select
        value={profileForm?.role || ""}
        onValueChange={(value) => handleProfileChange("role", value)}
      >
        <SelectTrigger id="profile-role">
          <SelectValue placeholder="Select role" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="USER">USER</SelectItem>
          <SelectItem value="ADMIN">ADMIN</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );

  const renderDateField = (key, label) => (
    <div className="space-y-2">
      <Label htmlFor={`profile-${key}`}>{label}</Label>
      <Input
        id={`profile-${key}`}
        value={formatUnixDate(profileForm?.[key])}
        disabled
      />
    </div>
  );

  const fetchMonthlyAllowed = async () => {
    if (!selectedUser) return;
    setMonthlyLoading(true);
    setMonthlyError("");
    try {
      const res = await fetch(
        "https://printlogs-17738dc65798.herokuapp.com/bizVantage/admin/assessmentsAllowedMonthly",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(authToken ? { authorization: authToken } : {}),
          },
          body: JSON.stringify({ userId: getUserId(selectedUser) }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Failed to fetch monthly allowed.");
      }
      const items = data?.data || data?.allowed || data || [];
      setMonthlyAllowed(Array.isArray(items) ? items : []);
    } catch (e) {
      setMonthlyError(e.message || "Failed to fetch monthly allowed.");
      setMonthlyAllowed([]);
    } finally {
      setMonthlyLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTab === "monthlyAllowed" && selectedUser) {
      fetchMonthlyAllowed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab, selectedUserId]);

  const openMonthlyDialog = () => {
    setMonthlyForm({ month: "", year: "", allowed: "" });
    setMonthlyEditingItem(null);
    setMonthlyDialogOpen(true);
  };

  const openEditMonthly = (item) => {
    // item.monthYear expected like "May-2026"
    const [monthLabel, yearStr] = String(item.monthYear || "").split("-");
    const monthEntry = monthOptions.find((m) => m.label === monthLabel);
    const monthValue = monthEntry ? monthEntry.value : "";
    const allowed = item.allowedAssessments ?? item.allowed ?? item.count ?? "";
    setMonthlyForm({
      month: String(monthValue),
      year: String(yearStr || ""),
      allowed: String(allowed),
    });
    setMonthlyEditingItem(item);
    setMonthlyDialogOpen(true);
  };

  const closeMonthlyDialog = () => {
    setMonthlyDialogOpen(false);
    setMonthlyEditingItem(null);
  };

  const handleSaveMonthlyAllowed = async () => {
    const month = Number(monthlyForm.month);
    const year = Number(monthlyForm.year);
    const allowed = Number(monthlyForm.allowed);
    if (!month || !year || !Number.isFinite(allowed)) {
      alert("Please provide valid month, year and allowed number.");
      return;
    }
    try {
      setMonthlyLoading(true);
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const monthLabel = monthNames[month - 1] || String(monthlyForm.month);
      const payload = {
        userId: getUserId(selectedUser),
        monthYear: `${monthLabel}-${year}`,
        allowedAssessments: allowed,
      };
      const res = await fetch(
        "https://printlogs-17738dc65798.herokuapp.com/bizVantage/admin/assessmentsAllowedMonthly",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(authToken ? { authorization: authToken } : {}),
          },
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Failed to save monthly allowed.");
      }
      toast.success("Monthly allowed saved.");
      setMonthlyDialogOpen(false);
      fetchMonthlyAllowed();
    } catch (e) {
      toast.error(e.message || "Failed to save monthly allowed.");
    } finally {
      setMonthlyLoading(false);
    }
  };
  const handleUpdateMonthlyAllowed = async () => {
    const month = Number(monthlyForm.month);
    const year = Number(monthlyForm.year);
    const allowed = Number(monthlyForm.allowed);
    if (!month || !year || !Number.isFinite(allowed)) {
      alert("Please provide valid month, year and allowed number.");
      return;
    }

    try {
      setMonthlyLoading(true);
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const monthLabel = monthNames[month - 1] || String(monthlyForm.month);
      const payload = {
        userId: getUserId(selectedUser),
        monthYear: `${monthLabel}-${year}`,
        allowedAssessments: allowed,
      };
      const res = await fetch(
        "https://printlogs-17738dc65798.herokuapp.com/bizVantage/admin/assessmentsAllowedMonthly",
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(authToken ? { authorization: authToken } : {}),
          },
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || "Failed to save monthly allowed.");
      }
      toast.success("Monthly allowed saved.");
      setMonthlyDialogOpen(false);
      fetchMonthlyAllowed();
    } catch (e) {
      toast.error(e.message || "Failed to save monthly allowed.");
    } finally {
      setMonthlyLoading(false);
    }
  };

  return (
    <DashboardLayout>
      {(loading ||
        assessmentLoading ||
        intervieweeLoading ||
        profileLoading) && <FullScreenLoader />}
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <h1 className="text-3xl font-bold font-display tracking-tight text-foreground">
            Admin
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage platform users and account details.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(240px,20%)_1fr] ">
          <Card className="shadow-[var(--card-shadow)]">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm font-display">
                <UsersRound className="h-4 w-4 text-accent" />
                Users
              </CardTitle>
              <CardDescription>{users.length} total users</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input
                value={userSearch}
                onChange={(event) => setUserSearch(event.target.value)}
                placeholder="Search users..."
                aria-label="Search users"
              />
              {error ? (
                <p className="text-sm text-destructive">{error}</p>
              ) : users.length === 0 && !loading ? (
                <p className="text-sm text-muted-foreground">No users found.</p>
              ) : filteredUsers.length === 0 && !loading ? (
                <p className="text-sm text-muted-foreground">
                  No users match your search.
                </p>
              ) : (
                <div className="max-h-[calc(100vh-240px)] space-y-2 overflow-y-auto pr-1">
                  {filteredUsers.map((user) => {
                    const userId = getUserId(user);
                    const userName =
                      user?.fullName ||
                      user?.name ||
                      user?.companyName ||
                      "User";
                    const isSelected = selectedUserId === userId;
                    return (
                      <button
                        key={userId}
                        type="button"
                        onClick={() => setSelectedUserId(userId)}
                        className={cn(
                          "w-full rounded-lg border p-3 text-left transition-colors",
                          isSelected
                            ? "border-primary bg-primary/10"
                            : "hover:bg-muted",
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10">
                            <UserRound className="h-4 w-4 text-primary" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">
                              {userName}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                              {user?.email || "No email"}
                            </p>
                            {user?.superAdmin === true && (
                              <Badge className="mt-2 text-[10px]">
                                Super Admin
                              </Badge>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          <Card className=" shadow-[var(--card-shadow)]">
            <CardHeader>
              <CardTitle className="text-lg font-display">
                User Details
              </CardTitle>
              <CardDescription>
                Select a user from the list to view details.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedUser ? (
                <>
                  <Tabs defaultValue="assessments" className="w-full">
                    <TabsList className="grid w-full max-w-md grid-cols-3">
                      <TabsTrigger
                        value="assessments"
                        onClick={() => setSelectedTab("assessments")}
                      >
                        Assessments
                      </TabsTrigger>
                      <TabsTrigger
                        value="profile"
                        onClick={() => setSelectedTab("profile")}
                      >
                        Profile Data
                      </TabsTrigger>
                      <TabsTrigger
                        value="monthlyAllowed"
                        onClick={() => setSelectedTab("monthlyAllowed")}
                      >
                        Monthly Allowed
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="assessments" className="mt-5">
                      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(240px,30%)_1fr]">
                        <div className="rounded-lg border p-3">
                          <div className="mb-3 flex items-center justify-between">
                            <p className="flex items-center gap-2 text-sm font-medium">
                              <ClipboardCheck className="h-4 w-4 text-accent" />
                              Assessments
                            </p>
                            <Badge variant="secondary" className="text-[10px]">
                              {assessments.length}
                            </Badge>
                          </div>
                          <div className="relative mb-3">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              value={assessmentSearch}
                              onChange={(event) =>
                                setAssessmentSearch(event.target.value)
                              }
                              placeholder="Search assessments..."
                              className="pl-9"
                            />
                          </div>
                          {assessmentLoading ? (
                            <p className="text-sm text-muted-foreground">
                              Loading assessments...
                            </p>
                          ) : assessmentError ? (
                            <p className="text-sm text-destructive">
                              {assessmentError}
                            </p>
                          ) : assessments.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              No assessments found.
                            </p>
                          ) : filteredAssessments.length === 0 ? (
                            <p className="text-sm text-muted-foreground">
                              No assessments match your search.
                            </p>
                          ) : (
                            <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
                              {filteredAssessments.map((assessment) => {
                                const assessmentId = String(
                                  assessment?._id ||
                                    assessment?.id ||
                                    assessment?.assessmentId ||
                                    assessment?.companyName,
                                );
                                const isSelected =
                                  selectedAssessmentId === assessmentId;
                                const status = getAssessmentStatus(assessment);
                                return (
                                  <button
                                    key={assessmentId}
                                    type="button"
                                    onClick={() =>
                                      setSelectedAssessmentId(assessmentId)
                                    }
                                    className={cn(
                                      "w-full rounded-lg border p-3 text-left transition-colors",
                                      isSelected
                                        ? "border-primary bg-primary/10"
                                        : "hover:bg-muted",
                                    )}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <p className="truncate text-sm font-medium">
                                        {assessment?.companyName ||
                                          assessment?.title ||
                                          "Untitled assessment"}
                                      </p>
                                      <Badge
                                        variant="secondary"
                                        className={cn(
                                          "shrink-0 text-[10px]",
                                          status.toLowerCase() ===
                                            "completed" &&
                                            "bg-green-500/15 text-green-700",
                                          status.toLowerCase() !==
                                            "completed" &&
                                            "bg-amber-500/15 text-amber-700",
                                        )}
                                      >
                                        {status}
                                      </Badge>
                                    </div>
                                    <p className="mt-1 truncate text-xs text-muted-foreground">
                                      {assessment?.keyContact ||
                                        assessment?.contact ||
                                        assessment?.status ||
                                        "No contact"}
                                    </p>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        <div className="rounded-lg border p-4">
                          {selectedAssessment ? (
                            <div className="space-y-4">
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <h3 className="text-lg font-semibold">
                                    {selectedAssessment.companyName ||
                                      selectedAssessment.title ||
                                      "Untitled assessment"}
                                  </h3>
                                  <p className="text-sm text-muted-foreground">
                                    {selectedAssessment.keyContact ||
                                      selectedAssessment.contact ||
                                      "No contact listed"}
                                  </p>
                                </div>
                                <Badge variant="secondary">
                                  {getAssessmentStatus(selectedAssessment)}
                                </Badge>
                              </div>
                              <div className="grid gap-3 sm:grid-cols-2">
                                <div className="rounded-lg bg-muted/40 p-3">
                                  <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                                    <Building2 className="h-3.5 w-3.5" />
                                    Company
                                  </p>
                                  <p className="mt-1 text-sm font-medium">
                                    {selectedAssessment.companyName || "N/A"}
                                  </p>
                                </div>
                                <div className="rounded-lg bg-muted/40 p-3">
                                  <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                                    <FileText className="h-3.5 w-3.5" />
                                    Assessment ID
                                  </p>
                                  <p className="mt-1 break-all text-sm font-medium">
                                    {selectedAssessment.assessmentId ||
                                      selectedAssessment._id ||
                                      selectedAssessment.id ||
                                      "N/A"}
                                  </p>
                                </div>
                                <div className="rounded-lg bg-muted/40 p-3">
                                  <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                                    <Mail className="h-3.5 w-3.5" />
                                    Email
                                  </p>
                                  <p className="mt-1 break-words text-sm font-medium">
                                    {selectedAssessment.email || "N/A"}
                                  </p>
                                </div>
                                <div className="rounded-lg bg-muted/40 p-3">
                                  <p className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
                                    <Phone className="h-3.5 w-3.5" />
                                    Phone
                                  </p>
                                  <p className="mt-1 text-sm font-medium">
                                    {selectedAssessment.phone || "N/A"}
                                  </p>
                                </div>
                              </div>
                              {(selectedAssessment.assessmentFile ||
                                selectedAssessment.assessmentAiPolicyPdf) && (
                                <div className="flex flex-wrap gap-3 border-t pt-4">
                                  {selectedAssessment.assessmentFile && (
                                    <Button asChild variant="outline" size="sm">
                                      <a
                                        href={selectedAssessment.assessmentFile}
                                        target="_blank"
                                        rel="noreferrer"
                                        download
                                      >
                                        <Download className="mr-2 h-4 w-4" />
                                        Download Assessment
                                      </a>
                                    </Button>
                                  )}
                                  {selectedAssessment.assessmentAiPolicyPdf && (
                                    <Button asChild variant="outline" size="sm">
                                      <a
                                        href={
                                          selectedAssessment.assessmentAiPolicyPdf
                                        }
                                        target="_blank"
                                        rel="noreferrer"
                                        download
                                      >
                                        <Download className="mr-2 h-4 w-4" />
                                        Download AI Policy
                                      </a>
                                    </Button>
                                  )}
                                </div>
                              )}
                              <div className="space-y-3 border-t pt-4">
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                  <div>
                                    <p className="text-sm font-medium">
                                      Interviewees
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {interviewees.length} total interviewees
                                    </p>
                                  </div>
                                  <div className="relative w-full sm:w-72">
                                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                      value={intervieweeSearch}
                                      onChange={(event) =>
                                        setIntervieweeSearch(event.target.value)
                                      }
                                      placeholder="Search interviewees..."
                                      className="pl-9"
                                    />
                                  </div>
                                </div>
                                {intervieweeLoading ? (
                                  <p className="text-sm text-muted-foreground">
                                    Loading interviewees...
                                  </p>
                                ) : intervieweeError ? (
                                  <p className="text-sm text-destructive">
                                    {intervieweeError}
                                  </p>
                                ) : filteredInterviewees.length === 0 ? (
                                  <p className="text-sm text-muted-foreground">
                                    No interviewees found.
                                  </p>
                                ) : (
                                  <div className="grid gap-2 lg:grid-cols-2">
                                    {filteredInterviewees.map((interviewee) => {
                                      const intervieweeId =
                                        interviewee?.intervieweeId ||
                                        interviewee?._id ||
                                        interviewee?.id ||
                                        interviewee?.email ||
                                        interviewee?.name;
                                      return (
                                        <div
                                          key={intervieweeId}
                                          className="rounded-lg border p-3"
                                        >
                                          <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                              <p className="truncate text-sm font-medium">
                                                {interviewee?.name ||
                                                  interviewee?.fullName ||
                                                  "Interviewee"}
                                              </p>
                                              <p className="truncate text-xs text-muted-foreground">
                                                {interviewee?.role ||
                                                  interviewee?.position ||
                                                  interviewee?.department ||
                                                  "No role"}
                                              </p>
                                            </div>
                                            {interviewee?.status && (
                                              <Badge
                                                variant="secondary"
                                                className="shrink-0 text-[10px]"
                                              >
                                                {interviewee.status}
                                              </Badge>
                                            )}
                                          </div>
                                          <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                                            {interviewee?.email && (
                                              <p className="truncate">
                                                {interviewee.email}
                                              </p>
                                            )}
                                            {interviewee?.phone && (
                                              <p className="truncate">
                                                {interviewee.phone}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex min-h-[420px] items-center justify-center rounded-lg border border-dashed">
                              <p className="text-sm text-muted-foreground">
                                Select an assessment to view details.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="profile" className="mt-5">
                      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium">Profile Data</p>
                          <p className="text-xs text-muted-foreground">
                            Edit user details and save changes.
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {selectedUser?.superAdmin !== true && selectedUser ? (
                            <Button
                              variant="destructive"
                              onClick={() => setDeleteDialogOpen(true)}
                              disabled={profileLoading || deleteProfileLoading}
                            >
                              Delete
                            </Button>
                          ) : null}
                          <Button
                            onClick={handleUpdateProfile}
                            disabled={profileLoading}
                          >
                            {profileLoading ? "Updating..." : "Update"}
                          </Button>
                        </div>
                      </div>
                      {profileError && (
                        <p className="mb-4 text-sm text-destructive">
                          {profileError}
                        </p>
                      )}
                      <div className="grid gap-4 md:grid-cols-2">
                        {renderProfileField("fullName", "Full Name")}
                        {renderProfileField("phone", "Phone", "tel")}
                        {renderProfileField("email", "Email", "email", true)}
                        {renderProfileField("password", "Password", "password")}
                        {renderProfileField("img", "Profile Image URL")}
                        {renderProfileField("userId", "User ID", "text", true)}
                        {renderDateField("createdDate", "Created Date")}
                        {renderRoleField()}
                        {renderProfileField("active", "Active")}
                        {renderProfileField("companyLogo", "Company Logo URL")}
                        {renderProfileField("companyName", "Company Name")}
                        {renderDateField("lastUpdated", "Last Updated")}
                        {renderPlanOptionField()}
                        {renderProfileField(
                          "superAdmin",
                          "Super Admin",
                          "text",
                          true,
                        )}
                      </div>
                    </TabsContent>
                    <TabsContent value="monthlyAllowed" className="mt-5">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <div className="flex flex-col">
                          <div>
                            <p className="text-sm font-medium">
                              Monthly allowed assessments
                            </p>
                            <p className="text-xs text-muted-foreground">
                              View and add allowed assessments per month.
                            </p>
                          </div>
                          <div className="mt-2 flex items-center gap-4">
                            <div>
                              <p className="text-xs text-muted-foreground">
                                Plan
                              </p>
                              <p className="text-sm font-semibold">
                                {profileForm?.planOption ||
                                  selectedUser?.planOption ||
                                  "-"}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">
                                Created Date
                              </p>
                              <p className="text-sm font-semibold">
                                {formatUnixDate(
                                  profileForm?.createdDate ||
                                    selectedUser?.createdDate,
                                ) || "-"}
                              </p>
                            </div>
                          </div>
                        </div>
                        <Button onClick={openMonthlyDialog}>Add</Button>
                      </div>
                      {monthlyError && (
                        <p className="mb-4 text-sm text-destructive">
                          {monthlyError}
                        </p>
                      )}
                      {monthlyLoading ? (
                        <p className="text-sm text-muted-foreground">
                          Loading...
                        </p>
                      ) : monthlyAllowed.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                          No monthly entries found.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {monthlyAllowed.map((item, idx) => (
                            <div
                              key={idx}
                              className="flex items-center justify-between rounded-lg border p-3"
                            >
                              <div>
                                <p className="text-sm font-medium">
                                  {item.monthYear ||
                                    `${item.month || item.m || ""}/${item.year || item.y || ""}`}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  Allowed:{" "}
                                  {item.allowed ??
                                    item.count ??
                                    item.allowedAssessments ??
                                    "-"}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openEditMonthly(item)}
                                >
                                  Edit
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {monthlyDialogOpen ? (
                        <div className="fixed inset-0 z-50 flex items-center justify-center">
                          <div
                            className="absolute inset-0 bg-black/40"
                            onClick={closeMonthlyDialog}
                          />
                          <div className="z-50 w-full max-w-md rounded-lg bg-white p-6 shadow-lg">
                            <h3 className="mb-3 text-lg font-semibold">
                              {monthlyEditingItem
                                ? "Edit monthly allowed"
                                : "Add monthly allowed"}
                            </h3>
                            <div className="grid gap-3">
                              <div className="flex gap-2">
                                <div className="flex-1">
                                  <Label>Month</Label>
                                  <Select
                                    value={monthlyForm.month}
                                    onValueChange={(value) =>
                                      setMonthlyForm((s) => ({
                                        ...s,
                                        month: value,
                                      }))
                                    }
                                  >
                                    <SelectTrigger id="monthly-month">
                                      <SelectValue placeholder="Select month" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {monthOptions.map((m) => (
                                        <SelectItem
                                          key={m.value}
                                          value={m.value}
                                        >
                                          {m.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="flex-1">
                                  <Label>Year</Label>
                                  <Select
                                    value={monthlyForm.year}
                                    onValueChange={(value) =>
                                      setMonthlyForm((s) => ({
                                        ...s,
                                        year: value,
                                      }))
                                    }
                                  >
                                    <SelectTrigger id="monthly-year">
                                      <SelectValue placeholder="Select year" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {yearOptions.map((y) => (
                                        <SelectItem key={y} value={y}>
                                          {y}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div>
                                <Label>Allowed assessments</Label>
                                <Input
                                  value={monthlyForm.allowed}
                                  onChange={(e) =>
                                    setMonthlyForm((s) => ({
                                      ...s,
                                      allowed: e.target.value,
                                    }))
                                  }
                                  placeholder="Number allowed"
                                  type="number"
                                />
                              </div>
                              <div className="mt-4 flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  onClick={closeMonthlyDialog}
                                >
                                  Cancel
                                </Button>
                                {monthlyEditingItem ? (
                                  <Button
                                    onClick={handleUpdateMonthlyAllowed}
                                    disabled={monthlyLoading}
                                  >
                                    {monthlyLoading ? "Updating..." : "Update"}
                                  </Button>
                                ) : (
                                  <Button
                                    onClick={handleSaveMonthlyAllowed}
                                    disabled={monthlyLoading}
                                  >
                                    {monthlyLoading ? "Saving..." : "Save"}
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </TabsContent>
                  </Tabs>
                  <Dialog
                    open={deleteDialogOpen}
                    onOpenChange={(open) => setDeleteDialogOpen(open)}
                  >
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="font-display">
                          Delete User
                        </DialogTitle>
                        <DialogDescription>
                          Are you sure? This action cannot be undone.
                        </DialogDescription>
                      </DialogHeader>
                      <DialogFooter className="gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setDeleteDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={handleDeleteProfile}
                          disabled={deleteProfileLoading}
                        >
                          {deleteProfileLoading ? "Deleting..." : "Delete"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </>
              ) : (
                <div className="flex min-h-[360px] items-center justify-center rounded-lg border border-dashed">
                  <p className="text-sm text-muted-foreground">
                    Select a user to view account details.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Admin;
